import { useAuth } from '@clerk/nextjs';
import { ApiClient } from '@/services/api-client';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

// The key prefix used in localStorage to persist onboarding state per organization
const STORAGE_KEY_PREFIX = 'orsay_onboarding_tutorial_';

export type OnboardingStep = 'connect_integration' | 'create_agent' | 'create_sequence';

const ORDERED_STEPS: OnboardingStep[] = ['connect_integration', 'create_agent', 'create_sequence'];

/**
 * OnboardingState Interface
 * @property dismissed - Whether the user manually closed the wizard for the current organization.
 * @property completedSteps - List of steps that have been finished.
 * @property isSetupModeActive - Whether the wizard is currently in an active state.
 */
interface OnboardingState {
  dismissed: boolean;
  completedSteps: OnboardingStep[];
  isSetupModeActive: boolean;
}

const DEFAULT_STATE: OnboardingState = {
  dismissed: false,
  completedSteps: [],
  isSetupModeActive: true,
};

/**
 * Loads the onboarding state for a specific organization from localStorage.
 */
function loadState(orgId: string | undefined): OnboardingState {
  if (!orgId) return DEFAULT_STATE;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${orgId}`);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_STATE,
      ...parsed,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

/**
 * Saves the onboarding state for a specific organization to localStorage.
 * Dispatches a custom event so other hook instances in the same tab can sync.
 */
function saveState(orgId: string | undefined, state: OnboardingState) {
  if (!orgId) return;
  localStorage.setItem(`${STORAGE_KEY_PREFIX}${orgId}`, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent('onboarding-state-change', { detail: { orgId, state } }));
}

/**
 * Hook: useOnboardingTutorial
 *
 * This hook is the brain of the onboarding system. It:
 * 1. Checks if the current organization is "new" (no integrations, no agents).
 * 2. Persists the "dismissed" state locally so the user isn't annoyed twice.
 * 3. Polls the API to detect when the user has actually connected a phone or created an agent.
 */
export function useOnboardingTutorial() {
  const router = useRouter();
  const { orgId: authOrgId } = useAuth();

  // Local state initialized from localStorage
  const [state, setState] = useState<OnboardingState>(() => loadState(authOrgId || undefined));

  // Sync state if orgId changes (e.g. user switches organization)
  useEffect(() => {
    setState(loadState(authOrgId || undefined));
  }, [authOrgId]);

  // Sync state across hook instances in the same tab
  useEffect(() => {
    const handler = (e: Event) => {
      const { orgId, state: newState } = (e as CustomEvent).detail;
      if (orgId === authOrgId) {
        setState(newState);
      }
    };
    window.addEventListener('onboarding-state-change', handler);
    return () => window.removeEventListener('onboarding-state-change', handler);
  }, [authOrgId]);

  /**
   * DATA CHECK: We fetch integrations, agents, and sequences to see what's already there.
   * This allows us to auto-complete steps if the user does them outside the wizard.
   */
  const { data: integrations } = useQuery({
    queryKey: ['integration.getWhatsappProfiles'],
    queryFn: () => ApiClient.integration.getWhatsappProfiles(),
    enabled: !!authOrgId,
  });

  const { data: instagrams } = useQuery({
    queryKey: ['integration.getInstagramAccounts'],
    queryFn: () => ApiClient.integration.getInstagramAccounts(),
    enabled: !!authOrgId,
  });

  const { data: agents } = useQuery({
    queryKey: ['agent.list'],
    queryFn: () => ApiClient.agents.getAllAgents(),
    enabled: !!authOrgId,
  });

  const { data: sequences } = useQuery({
    queryKey: ['sequence.list'],
    queryFn: () => ApiClient.sequences.getSequences(),
    enabled: !!authOrgId,
  });

  /**
   * LOGIC: Compute which steps are actually completed based on real API data.
   */
  useEffect(() => {
    if (!integrations || !agents || !sequences) return;

    const completed: OnboardingStep[] = [];

    // Check for active integrations (WhatsApp or Instagram)
    const hasActiveWA = integrations?.data?.some((p) => p.ProfileStatus === 'ACTIVE');
    const hasActiveIG = (instagrams?.data?.Accounts?.length ?? 0) > 0;

    if (hasActiveWA || hasActiveIG) completed.push('connect_integration');

    // Check for agents
    if (agents?.data?.Agents && agents.data.Agents.length > 0) completed.push('create_agent');

    // Check for sequences
    if (sequences?.data && sequences.data.length > 0) completed.push('create_sequence');

    // Update state if changes detected
    if (JSON.stringify(completed) !== JSON.stringify(state.completedSteps)) {
      const newState = { ...state, completedSteps: completed };
      setState(newState);
      saveState(authOrgId || undefined, newState);
    }
  }, [integrations, instagrams, agents, sequences, authOrgId, state]);

  /**
   * ACTION: Dismiss the wizard permanently for this organization.
   */
  const dismiss = useCallback(() => {
    const newState = { ...state, dismissed: true };
    setState(newState);
    saveState(authOrgId || undefined, newState);
  }, [state, authOrgId]);

  /**
   * ACTION: Force navigation to a specific step.
   */
  const goToStep = useCallback(
    (step: OnboardingStep) => {
      // Map internal step keys to the Wizard's step numbers
      const stepMap: Record<OnboardingStep, number> = {
        connect_integration: 1,
        create_agent: 4, // Successful connection screen leads to agent creation
        create_sequence: 4,
      };

      localStorage.setItem('setup_wizard_step', stepMap[step].toString());
      localStorage.setItem('setup_wizard_waiting', 'false');

      router.push({
        pathname: '/onboarding',
        query: { ...router.query, forceSetup: 'true' },
      });
    },
    [router]
  );

  /**
   * COMPUTED VALUES
   */
  const isComplete = state.completedSteps.length === ORDERED_STEPS.length;
  const isSetupModeVisible = !state.dismissed && !isComplete && !!authOrgId;
  const currentStep =
    ORDERED_STEPS.find((step) => !state.completedSteps.includes(step)) ||
    ORDERED_STEPS[ORDERED_STEPS.length - 1];
  const progress = (state.completedSteps.length / ORDERED_STEPS.length) * 100;

  return {
    isSetupModeVisible,
    isVisible: isSetupModeVisible, // Alias for backward compatibility
    completedSteps: state.completedSteps,
    dismiss,
    goToStep,
    steps: ORDERED_STEPS,
    currentStep,
    progress,
    state,
  };
}
